<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRoute } from "vue-router";
const iconPlay = require("@/assets/img/icon-play.png");
const iconPause = require("@/assets/img/icon-pause.png");
const iconClose = require("@/assets/img/icon-close.png");
import {
  getAudioPlayerInfo,
  isShowAudioPlayer,
  checkPlayerSupport
} from "@/utils/audioPlayer";
import "@/plugins/bridge.js";

const props = defineProps({
  audioUrl: {
    type: String,
    default: ""
  },
  imgUrl: {
    type: String,
    default: ""
  },
  title: {
    type: String,
    default: ""
  }
});
const route = useRoute();

// 默认音频域名
const defaultAudioDomain =
  process.env.VUE_APP_BASE_URL || "https://zsrbapp.zsnews.cn";

// 补全音频 URL 域名
const normalizeAudioUrl = (url) => {
  const rawUrl = (url || "").trim();
  if (!rawUrl) return "";

  const hasDomain = /^(https?:)?\/\//i.test(rawUrl);
  const hasScheme = /^[a-z][a-z\d+\-.]*:/i.test(rawUrl);
  if (hasDomain || hasScheme) return rawUrl;

  const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
  return `${defaultAudioDomain}${path}`;
};
const footerBottom = ref(0); // footer 高度，用于 radio-play-control 的 bottom 偏移
let footerObserver = null;

const isControlHidden = ref(false); // 是否缩小播放控制栏
let lastScrollTop = 0;

const goAudioDetailSupport = ref(false); // 是否为9.1.23版本，可以跳到音频详情页

const handleScroll = () => {
  // 判断radioPlayControl是否存在
  if (!audioShow.value) {
    return;
  } else {
    const st = document.documentElement.scrollTop;
    if (st > lastScrollTop && st > 0) {
      isControlHidden.value = true;
    }
    lastScrollTop = st <= 0 ? 0 : st;
  }
};

const showControl = () => {
  isControlHidden.value = false;
};

const checkEnv = ref(false); // 是否检查环境
const isSupport = ref(false); // 是否支持桥接
const hasBridgeInfo = ref(false); // 是否有桥接播放信息
const audioRef = ref(null); // 音频对象
const audioShow = ref(false); // 底部播放栏是否显示
const playStatus = ref("pause"); // 播放状态
const isResume = ref(false); // 是否是继续播放, 第一次播放时为false
const currentItem = ref({
  image: props.imgUrl,
  title: props.title,
  url: normalizeAudioUrl(props.audioUrl),
  source: "newsDetail"
}); // 当前播放音频信息

// 方法：同步props到currentItem
const syncPropsToCurrentItem = () => {
  if (hasBridgeInfo.value) {
    return;
  }
  currentItem.value = {
    image: props.imgUrl,
    title: props.title,
    url: normalizeAudioUrl(props.audioUrl),
    source: "newsDetail"
  };
};

// 2. 监听props变化，实时同步（解决props异步更新的问题）
watch(
  // 监听需要的props字段
  [() => props.audioUrl, () => props.imgUrl, () => props.title],
  () => {
    syncPropsToCurrentItem();
  },
  { immediate: true } // 立即执行一次
);

// 底部栏播放/暂停切换按钮
const handleControl = async (status) => {
  // 点击父组件的播放按钮, 设置当前播放音频信息
  if (status === "play") {
    // 父组件点击，设置音频
    // isControlHidden.value = true;
    const footerEl = document.querySelector(".footer");
    if (footerEl) {
      footerBottom.value = footerEl.scrollHeight;
    }
    currentItem.value = {
      image: props.imgUrl,
      title: props.title,
      url: normalizeAudioUrl(props.audioUrl),
      source: "newsDetail",
      infoId: route.params.id
    };
  }
  audioShow.value = true;
  if (checkEnv.value && isSupport.value) {
    // 支持桥接功能
    if (status === "play") {
      // 父组件点击：正在播放中无需改变状态
      if (isResume.value) {
        if (playStatus.value === "play" || playStatus.value === "resume") {
          return;
        } else {
          // 暂停时点击：已播放过则续播，否则首次播放
          playStatus.value = isResume.value ? "resume" : "play";
        }
      } else {
        playStatus.value = "play";
      }
      await bridgePlayer();
      isResume.value = true;
    } else {
      // 底部栏切换：播放中/续播中 -> 暂停，暂停 -> 续播
      if (playStatus.value === "play" || playStatus.value === "resume") {
        playStatus.value = "pause";
      } else {
        playStatus.value = "resume";
      }
      await bridgePlayer();
    }
  } else {
    // 不支持桥接功能, 调用h5播放方法
    if (status === "play") {
      playStatus.value = "play";
    } else {
      playStatus.value = playStatus.value === "play" ? "pause" : "play";
    }
    h5Player(status);
  }
};

const bridgePlayer = async () => {
  window.ZsrbApp.call("controlAudioPlayer", {
    action: playStatus.value,
    url: currentItem.value.url || "",
    image: currentItem.value.image || "",
    title: currentItem.value.title || "",
    source: currentItem.value.source || "",
    infoId: currentItem.value.infoId || ""
  });
};

const h5Player = async () => {
  if (!audioRef.value) {
    createAudio(currentItem.value.url);
  } else {
    if (playStatus.value === "play") {
      audioRef.value.play().catch(() => {
        playStatus.value = "pause";
      });
    } else {
      audioRef.value.pause();
    }
  }
};

const createAudio = () => {
  destroyAudio();
  audioRef.value = document.createElement("audio");
  audioRef.value.src = currentItem.value.url;
  audioRef.value.play().catch(() => {
    playStatus.value = "pause";
  });
};

const destroyAudio = () => {
  if (!audioRef.value) {
    return;
  }
  audioRef.value.pause();
  audioRef.value.src = "";
  audioRef.value.remove();
  audioRef.value = null;
  audioShow.value = false;
};

// 关闭音频
const closeAudio = async () => {
  playStatus.value = "pause";
  isResume.value = false;
  audioShow.value = false;

  if (checkEnv.value && isSupport.value) {
    // 支持桥接功能
    window.ZsrbApp.call("controlAudioPlayer", {
      action: "close",
      url: "",
      image: "",
      title: "",
      source: "",
      infoId: ""
    });
    isShowAudioPlayer(0);
  } else {
    // 不支持桥接
    destroyAudio();
  }
};

const checkPlaying = async () => {
  // 判断是否支持桥接，支持桥接则获取正在播放的音频
  if (checkEnv.value && isSupport.value) {
    const info = await getAudioPlayerInfo();
    if (info.code === 1 || info.code === "1") {
      hasBridgeInfo.value = true;
      currentItem.value.image = info.image;
      currentItem.value.title = info.title;
      currentItem.value.url = info.url;
      currentItem.value.source = info.source;
      currentItem.value.infoId = info.infoId;
      if (info.playStatus) {
        playStatus.value = info.playStatus === 1 ? "play" : "pause";
      } else {
        playStatus.value = "play";
      }
      // 播放状态与当前播放音频信息匹配时，更新播放状态,并且显示播放器
      audioShow.value = true;
      isShowAudioPlayer(0);
      return;
    } else {
      isShowAudioPlayer(0);
      closeAudio();
      audioShow.value = false; // 没有播放情况隐藏播放器
    }
    isShowAudioPlayer(0);
  }
};

const toAudioDetail = () => {
  window.ZsrbApp.call("goAudioDetail", {});
};

const initial = async () => {
  await nextTick(async () => {
    isSupport.value = await checkPlayerSupport("9.1.18");
    goAudioDetailSupport.value = await checkPlayerSupport("9.1.23");
    checkEnv.value = true;
    checkPlaying();
    const footerEl = document.querySelector(".footer");
    if (footerEl) {
      footerBottom.value = footerEl.scrollHeight;
      footerObserver = new ResizeObserver(() => {
        footerBottom.value = footerEl.scrollHeight;
      });
      footerObserver.observe(footerEl);
    }
  });
};

onMounted(async () => {
  window.addEventListener("scroll", handleScroll);
  await initial();
  if (goAudioDetailSupport.value) {
    window.ZsrbApp.call("viewWillAppear", {}, () => {
      isShowAudioPlayer(0);
      initial();
    });
    // 兼容iOS多次回调
    window.ZsrbApp.register("viewWillAppear", () => {
      isShowAudioPlayer(0);
      initial();
    });
  }
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  if (footerObserver) {
    footerObserver.disconnect();
    footerObserver = null;
  }
  // h5环境离开页面时销毁音频
  destroyAudio();
});

defineExpose({
  handleControl,
  playStatus
});
</script>

<template>
  <div>
    <div
      ref="radioPlayControl"
      id="radio-play-control"
      class="radio-play-control"
      v-show="audioShow"
      :class="{ 'radio-play-control-hide': isControlHidden }"
      :style="{ bottom: footerBottom + 10 + 'px' }"
      @click="showControl"
    >
      <div class="flex align-center flex-1">
        <div class="play-image">
          <img
            :src="currentItem.image"
            :class="{
              active: playStatus === 'play' || playStatus === 'resume'
            }"
            alt=""
          />
        </div>
      </div>
      <div class="flex align-center">
        <div
          v-if="goAudioDetailSupport"
          class="radio-text"
          @click="toAudioDetail"
        >
          听新闻兑积分
        </div>
        <img
          @click="handleControl()"
          class="u-control"
          :src="
            playStatus === 'play' || playStatus === 'resume'
              ? iconPause
              : iconPlay
          "
          alt=""
        />
        <img
          @click="closeAudio"
          class="u-control"
          :src="iconClose"
          alt=""
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/style/audio.scss" as audio;
</style>
